import React, { useState } from 'react';
import { List, useTable, ShowButton } from '@refinedev/antd';
import { Table, Space, Tag, Input, Image, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { AdminWorkoutLogListItemDto } from '@wellverse/shared-ts';
import { WorkoutLogSource } from '@wellverse/shared-ts';
import { resolveCmsMediaUrl } from '../../utils/media-url';
import { GpsLocation } from '../../components/GpsLocation';

const { Text } = Typography;

const sourceColors: Record<WorkoutLogSource, string> = {
  [WorkoutLogSource.MANUAL]: '#1890ff',
  [WorkoutLogSource.SCAN]: '#722ed1',
  [WorkoutLogSource.PLAN]: '#3ecf8e',
};

const tagStyle = (color: string) => ({
  borderColor: color,
  color,
  background: `${color}15`,
  borderRadius: 9999,
  padding: '2px 12px',
  fontWeight: 500,
});

const truncateUserId = (id: string) => {
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}...${id.slice(-4)}`;
};

export const WorkoutLogList: React.FC = () => {
  const [searchText, setSearchText] = useState('');

  const { tableProps } = useTable<AdminWorkoutLogListItemDto>({
    resource: 'workout-logs',
    filters: {
      permanent: [{ field: 'search', operator: 'contains', value: searchText }],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  return (
    <List resource="workout-logs">
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          prefix={<SearchOutlined />}
          placeholder="Search by notes..."
          defaultValue={searchText}
          onSearch={(value) => setSearchText(value)}
          allowClear
          style={{ width: 320 }}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1400 }}>
        <Table.Column
          title="Image"
          width={72}
          render={(_: unknown, record: AdminWorkoutLogListItemDto) => {
            const url = resolveCmsMediaUrl(record.coverPhotoUrl);
            return url ? (
              <Image
                src={url}
                alt=""
                width={48}
                height={48}
                style={{ objectFit: 'cover', borderRadius: 6 }}
                fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Crect fill='%23f0f0f0' width='48' height='48'/%3E%3C/svg%3E"
                preview={{ mask: false }}
              />
            ) : (
              <Text type="secondary">—</Text>
            );
          }}
        />
        <Table.Column
          dataIndex="userId"
          title="User ID"
          render={(id: string) => truncateUserId(id)}
        />
        <Table.Column
          title="Equipment"
          width={220}
          render={(_: unknown, record: AdminWorkoutLogListItemDto) => {
            if (!record.equipment?.length) return <Text type="secondary">—</Text>;
            return (
              <Space direction="vertical" size={2}>
                {record.equipment.map((eq) => (
                  <Link key={eq.id} to={`/equipment-catalog/show/${eq.id}`}>
                    {eq.name}
                  </Link>
                ))}
              </Space>
            );
          }}
        />
        <Table.Column
          dataIndex="source"
          title="Source"
          filters={Object.values(WorkoutLogSource).map((s) => ({ text: s, value: s }))}
          onFilter={(value, record: AdminWorkoutLogListItemDto) => record.source === value}
          render={(source: WorkoutLogSource) => (
            <Tag style={tagStyle(sourceColors[source] || '#898989')}>{source}</Tag>
          )}
        />
        <Table.Column
          title="Location"
          width={160}
          render={(_: unknown, record: AdminWorkoutLogListItemDto) => (
            <GpsLocation variant="cell" latitude={record.latitude} longitude={record.longitude} />
          )}
        />
        <Table.Column
          dataIndex="startedAt"
          title="Started At"
          render={(date: string) => (date ? new Date(date).toLocaleString() : '—')}
        />
        <Table.Column
          dataIndex="duration"
          title="Duration"
          render={(v: number | undefined) => (v != null ? `${v} min` : '—')}
        />
        <Table.Column dataIndex="totalSets" title="Total Sets" />
        <Table.Column dataIndex="totalReps" title="Total Reps" />
        <Table.Column
          dataIndex="caloriesBurned"
          title="Calories"
          render={(v: number | undefined) => (v != null ? v : '—')}
        />
        <Table.Column
          dataIndex="completedAt"
          title="Completed"
          filters={[
            { text: 'Yes', value: true },
            { text: 'No', value: false },
          ]}
          onFilter={(value, record: AdminWorkoutLogListItemDto) =>
            (record.completedAt != null) === value
          }
          render={(date: string | undefined) => (
            <Tag style={tagStyle(date ? '#3ecf8e' : '#898989')}>
              {date ? 'Yes' : 'No'}
            </Tag>
          )}
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_: unknown, record: AdminWorkoutLogListItemDto) => (
            <Space>
              <ShowButton hideText size="small" recordItemId={record.id} resource="workout-logs" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
